import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { Input, Select, Modal, Button, message } from "antd";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import { useItemsFine } from "../_actions/app/items";
import { useActualTrendIdeaGeneration } from "../_actions/bot";
import { CalendarCard } from "../content_calender_new/SocialMediaCards";
import styles from "./styles/TrendsNew.module.css";
import {
    useTrendIdeaGeneration,
    useXTrendsGeneration,
    botSecureAxios,
} from "../_actions/bot";
import IdeaCanvasModal from "../content_calender_new/IdeaCanvasModal";
import IdeaCanvasBox from "../content_calender_new/IdeaCanvasBox";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { RiseOutlined } from "@ant-design/icons";
import { toLower } from "lodash";
import FireFill from "./fire";
import LoadingLottie from "../common/LoadingLottie";
import TrendLoadingLottie from "../media/animations/Searching.json";
import LoadingBox from "../common/LoadingBox";

const { Option } = Select;

export default function TrendsNew({ me, isPanel = false }) {
    const [selectedTrend, setSelectedTrend] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [region, setRegion] = useState(null);
    const [filteredTrends, setFilteredTrends] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedIndustry, setSelectedIndustry] = useState("all");
    const [trendsData, setTrendsData] = useState([]);
    const [isPolling, setIsPolling] = useState(false);

    // Fetch regions data
    const {
        data,
        isLoading: loadingRegion,
        isSuccess: successRegion,
    } = useItemsFine(
        "qdb_region",
        "fields=id,status,type,name&filter[status][_eq]=published"
    );

    // Setup the trend idea generation hook
    const genTrendIdea = useActualTrendIdeaGeneration(
        (result) => {
            console.log("Received generated ideas:", result);
        },
        () => {
            console.log("Generation completed");
        }
    );

    // Setup the trends generation hook
    const genNewTrends = useXTrendsGeneration(
        (result) => {
            if (result?.code === "PENDING") {
                console.log(
                    "Trend fetching is pending, starting background poll..."
                );
                setIsPolling(true);
            } else {
                console.log("trends refreshed", result);
                setTrendsData(result?.data || []);
                setIsPolling(false);
            }
        },
        () => {
            console.log("new trends saved in db");
        }
    );

    // Polling query when trend fetching is PENDING
    useQuery(
        ["pollTrends", region?.id],
        () =>
            botSecureAxios
                .post(`/trends/get_new_trends`, {
                    region_id: region?.id,
                    region: region?.name,
                })
                .then((res) => res.data),
        {
            enabled: isPolling && !!region,
            refetchInterval: 10000,
            onSuccess: (result) => {
                if (result?.code === "PENDING") {
                    console.log("Trend fetching still pending...");
                } else {
                    console.log("Polling finished, trends ready", result);
                    setIsPolling(false);
                    setTrendsData(result?.data || []);
                }
            },
            onError: (err) => {
                console.error("Polling error:", err);
                setIsPolling(false);
            },
        }
    );

    // Handle region change and fetch trends
    useEffect(() => {
        if (region) {
            genNewTrends.mutate({ region_id: region.id, region: region.name });
            setSelectedCategory("all");
            setSelectedIndustry("all");
            setSearchText("");
        }
    }, [region]);

    // Initial load - set first region as default when data arrives
    useEffect(() => {
        if (data && data.length > 0 && !region) {
            const global = data.find((r) => r.name?.toLowerCase() === "global");

            if (global) setRegion(global);
        }
    }, [data, region]);

    // Get unique categories and industries for filters
    const categories = [
        "all",
        ...new Set(
            (trendsData || [])
                .filter((item) => item?.category)
                .map((item) => item.category)
        ),
    ];

    const industries = [
        "all",
        ...new Set(
            (trendsData || [])
                .filter((item) => item?.industry || item?.industries_temp)
                .map((item) => item.industry || item.industries_temp)
        ),
    ];

    // Filter trends based on search and filters
    useEffect(() => {
        let results = trendsData || [];

        if (searchText) {
            results = results.filter(
                (trend) =>
                    trend.title
                        ?.toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    trend.content
                        ?.toLowerCase()
                        .includes(searchText.toLowerCase())
            );
        }

        if (selectedCategory !== "all") {
            results = results.filter(
                (trend) => trend.category === selectedCategory
            );
        }

        if (selectedIndustry !== "all") {
            results = results.filter(
                (trend) => trend.industries_temp === selectedIndustry
            );
        }

        results = results.sort((a, b) => b.intensity - a.intensity);
        setFilteredTrends(results);
    }, [searchText, selectedCategory, selectedIndustry, trendsData]);

    const handleCardClick = (trend, fromButton = false) => {
        if (!fromButton) {
            setSelectedTrend(trend);
            setModalVisible(true);
        }

        if (trend && trend.id) {
            console.log(`Generating ideas for trend: ${trend.id}`);
            genTrendIdea.mutate({ id: trend.id });
        }
    };

    const isLoadingTrends = genNewTrends.isLoading || isPolling;

    const content = (
        <div
            className={styles.trendsContainer}
            style={isPanel ? { padding: 0 } : {}}
        >
            <div className={styles.filterSection}>
                {/* Region selector */}
                {successRegion && (
                    <Select
                        style={{ width: 200 }}
                        value={region?.name || "Select Region"}
                        onChange={(value) => {
                            const selectedRegion = data.find(
                                (region) => region.id === value
                            );
                            if (selectedRegion) {
                                setRegion(selectedRegion);
                            }
                        }}
                        options={data?.map((region) => ({
                            value: region.id,
                            label: region.name,
                        }))}
                    />
                )}

                {/* Category filter */}
                {!isLoadingTrends && (
                    <Select
                        defaultValue="all"
                        onChange={setSelectedCategory}
                        className={styles.filterSelect}
                    >
                        {categories.map((category) => (
                            <Option key={category} value={category}>
                                {category === "all"
                                    ? "All Categories"
                                    : category}
                            </Option>
                        ))}
                    </Select>
                )}

                {/* Industry filter */}
                {!isLoadingTrends && (
                    <Select
                        defaultValue="all"
                        onChange={setSelectedIndustry}
                        className={styles.filterSelect}
                    >
                        {industries.map((industry) => (
                            <Option key={industry} value={industry}>
                                {industry === "all"
                                    ? "All Industries"
                                    : industry}
                            </Option>
                        ))}
                    </Select>
                )}

                {/* Search input */}
                {!isLoadingTrends && (
                    <div style={{ marginLeft: "auto" }}>
                        <Input
                            placeholder="Search trends..."
                            prefix={<SearchOutlined />}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 304 }}
                        />
                    </div>
                )}
            </div>

            {/* Loading indicator */}
            {isLoadingTrends ? (
                <LoadingLottie
                    file={TrendLoadingLottie}
                    style={{
                        width: 450,
                        height: 300,
                        marginTop: "-20px",
                    }}
                    wrapper_style={{
                        minHeight: "60vh",
                        width: "100%",
                    }}
                    text={
                        isPolling
                            ? "Analyzing millions of data points... This might take 2-3 minutes."
                            : "Discovering new trends..."
                    }
                    text_style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666",
                        marginTop: "-30px",
                    }}
                />
            ) : (
                <>
                    <div className={styles.trendsGrid}>
                        {filteredTrends.map((trend) => (
                            <div
                                key={trend.id}
                                className={`${styles.trendCard} ${
                                    selectedTrend?.id === trend.id &&
                                    genTrendIdea.isLoading
                                        ? styles.loadingCard
                                        : ""
                                }`}
                                onClick={() => handleCardClick(trend)}
                            >
                                {selectedTrend?.id === trend.id &&
                                    genTrendIdea.isLoading && (
                                        <div
                                            className={
                                                styles.cardLoadingIndicator
                                            }
                                        >
                                            <LoadingOutlined spin />
                                        </div>
                                    )}
                                <div className={styles.cardContent}>
                                    <div className={styles.titleRow}>
                                        <div>
                                            <h2 className={styles.trendTitle}>
                                                {trend.title}
                                            </h2>
                                            <div className={styles.metaItem}>
                                                <span
                                                    className={styles.metaValue}
                                                >
                                                    {trend.trend_source ||
                                                        "General"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={styles.intensityBadge}>
                                            <FireFill
                                                intensity={trend.intensity}
                                            />
                                            <span
                                                className={
                                                    styles.intensityValue
                                                }
                                            >
                                                {trend.intensity * 10}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredTrends.length === 0 && !isLoadingTrends && (
                        <div className={styles.noResults}>
                            No trends found matching your criteria
                        </div>
                    )}
                </>
            )}

            {/* Trend Detail Modal */}
            <TrendDetailModal
                selectedTrend={selectedTrend}
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
                isGeneratingIdeas={genTrendIdea.isLoading}
                result={genTrendIdea.data}
                me={me}
            />
        </div>
    );

    if (isPanel) return content;

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading="Qureal Trends"
                    icon={<RiseOutlined />}
                />
            }
        >
            {content}
        </QurealSection>
    );
}

function TrendDetailModal({
    selectedTrend,
    modalVisible,
    setModalVisible,
    isGeneratingIdeas = false,
    result,
    me,
}) {
    const [selectedIdea, setSelectedIdea] = useState(null);
    const [generatedIdea, setGeneratedIdea] = useState(null);
    const [openIdea, setOpenIdea] = useState(false);

    const mutationIdeaGen = useTrendIdeaGeneration((result) => {
        console.log(result, "Gen Idea Result");
        if (result?.success === true && result?.data?.status === "stuck") {
            message.error("Template have issue");
            setSelectedIdea(null);
        } else {
            setGeneratedIdea(result?.data);
            setOpenIdea(true);
            setSelectedIdea(null);
        }
    });

    const onIdeaSelect = (idea) => {
        const out = {
            idea: idea,
            data_source_id: result?.data?.id,
        };
        mutationIdeaGen.mutate(out);
        setGeneratedIdea(null);
        console.log(out, "on Selected Idea");
    };

    const renderContent = (content) => {
        if (!content) return "No content available";

        try {
            const contentObj =
                typeof content === "string" ? JSON.parse(content) : content;

            if (contentObj.text || contentObj.fact) {
                return (
                    <>
                        {contentObj.text && <p>{contentObj.text}</p>}
                        {contentObj.fact && (
                            <div className={styles.factBox}>
                                <strong>Fact:</strong> {contentObj.fact}
                            </div>
                        )}
                    </>
                );
            }

            return content;
        } catch (e) {
            return content;
        }
    };

    return (
        <Modal
            title={selectedTrend?.title}
            open={modalVisible}
            onCancel={() => !isGeneratingIdeas && setModalVisible(false)}
            footer={null}
            width={700}
            className={styles.trendModal}
            maskClosable={!isGeneratingIdeas}
            style={{ top: 20 }}
        >
            {selectedTrend && (
                <div className={styles.trendDetail}>
                    <div className={styles.detailHeader}>
                        <div className={styles.detailCategory}>
                            {selectedTrend.category}
                        </div>
                        <div className={styles.detailSource}>
                            Source: {selectedTrend.trend_source}
                        </div>
                    </div>
                    <div className={styles.detailContent}>
                        {renderContent(selectedTrend.content)}
                    </div>

                    <div className={styles.detailMeta}>
                        <div className={styles.metaRow}>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>
                                    Intensity
                                </span>
                                <span className={styles.metaValue}>
                                    {selectedTrend.intensity * 10}%
                                </span>
                            </div>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>
                                    Industry
                                </span>
                                <span className={styles.metaValue}>
                                    {selectedTrend.industries_temp}
                                </span>
                            </div>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>
                                    Expires
                                </span>
                                <span className={styles.metaValue}>
                                    {selectedTrend.expiry_date === 0 ||
                                    !selectedTrend.expiry_date
                                        ? "Ongoing"
                                        : new Date(
                                              selectedTrend.expiry_date
                                          ).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {isGeneratingIdeas && (
                        <LoadingBox text="Generating Ideas" height="150px" />
                    )}

                    {/* Generated Ideas Section */}
                    {result?.data?.generated_ideas?.length > 0 && (
                        <>
                            <h3 className={styles.ideasTitle}>
                                Generated Ideas
                            </h3>
                            <div className={styles.ideas_wrapper}>
                                {result.data.generated_ideas.map(
                                    (idea, index) => (
                                        <CalendarCard
                                            key={index}
                                            item={{
                                                ...idea,
                                                content: idea?.idea,
                                                // platform: toLower(
                                                //     idea?.platform
                                                // ),
                                                platforms: [
                                                    {
                                                        post_type:
                                                            idea?.content_type,
                                                        posting_time:
                                                            idea?.posting_time,
                                                        status: "IDEA",
                                                    },
                                                ],
                                            }}
                                            onClick={() => {
                                                setSelectedIdea({
                                                    ...idea,
                                                    index: index,
                                                });
                                                onIdeaSelect(idea);
                                            }}
                                            loading={
                                                selectedIdea?.idea ===
                                                idea?.idea
                                            }
                                        />
                                    )
                                )}
                            </div>
                        </>
                    )}

                    {generatedIdea && (
                        <IdeaCanvasBox
                            key={generatedIdea?.id || generatedIdea}
                            open={openIdea}
                            setOpen={setOpenIdea}
                            data={generatedIdea}
                            entry_id={generatedIdea?.id}
                            me={me}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
}
